(function () {

  const CONFIG = Object.freeze({
    STOREFRONT_TOKEN: '3db91ab260e51eff5c4d4f05b392910f',
    STOREFRONT_URL: 'https://fullhousemodern.com/api/2024-01/graphql.json',
    TRADE_DISCOUNT_RATE: 0.10,
    TRADE_DISCOUNT_TITLE: 'TRADE DISCOUNT',
    TRADE_CUSTOMER_TAG: 'Trade',
    TRADE_COLLECTION_HANDLE: 'trade-discount',
    PRICE_SELECTOR: '.product-details .tww-price__current [data-price]',
    COMPARE_AT_SELECTOR: '[data-price-compare-container]',
    VARIANT_SELECTION_SELECTOR: 'variant-selection[data-variant-selection]',
    VARIANT_SELECT_SELECTOR: 'select[name="id"]',
    CURRENCY_LOCALE: 'en-CA',
    CURRENCY_CODE: 'CAD',
    RUN_CHECK_DEBOUNCE: 300,
    OBSERVER_DEBOUNCE: 300,
    VARIANT_CHANGE_DELAY: 100,
    CART_LINES_BATCH_SIZE: 250,
    CACHE_TTL_MS: 5 * 60 * 1000,
    CACHE_PREFIX: 'tww-mf-cache-',
    CLEARANCE_THRESHOLD_PCT: 31,
  });

  if (window.__twwGridInit && window.__twwProductInit) return;

  // ============================================================
  // SHARED UTILITIES
  // ============================================================

  function isTradeCustomer(card) {
    const customer = window.Shopify?.customer;
    if (!customer) return false;

    const tags = Array.isArray(customer.tags)
      ? customer.tags
      : (customer.tags ?? '').split(',').map(t => t.trim());

    if (!tags.includes(CONFIG.TRADE_CUSTOMER_TAG)) return false;

    if (card) {
      const collections = (card.dataset.collections ?? '')
        .split(',')
        .map(c => c.trim());

      if (!collections.includes(CONFIG.TRADE_COLLECTION_HANDLE)) {
        return false;
      }
    }

    return true;
  }

  function getBadgeLabel(pct) {
    return pct >= CONFIG.CLEARANCE_THRESHOLD_PCT
      ? 'Clearance'
      : 'Sale';
  }

  function getBadgeClass(pct) {
    return pct >= CONFIG.CLEARANCE_THRESHOLD_PCT
      ? 'product-badge--clearance'
      : 'product-badge--sale';
  }

  function getPriceUIClass(pct) {
    return pct >= CONFIG.CLEARANCE_THRESHOLD_PCT
      ? 'clearance'
      : 'sale';
  }

  function formatPrice(amount) {
    const moneyFormat =
      window.theme?.moneyFormat ||
      window.Shopify?.money_format ||
      document.querySelector('[data-money-format]')?.dataset.moneyFormat ||
      '${{amount}}';

    const rounded = Math.ceil(amount);
    const stripCents = str => str.replace(/\.00$/, '');

    if (window.Shopify?.formatMoney) {
      try {
        return stripCents(
          window.Shopify.formatMoney(rounded * 100, moneyFormat)
        );
      } catch (e) {
        console.warn('[TWW] formatMoney failed, using fallback:', e);
      }
    }

    return stripCents(
      new Intl.NumberFormat(CONFIG.CURRENCY_LOCALE, {
        style: 'currency',
        currency: CONFIG.CURRENCY_CODE,
      }).format(rounded)
    );
  }

  function formatRange(low, high) {
    if (Math.ceil(low) === Math.ceil(high)) {
      return formatPrice(low);
    }

    return `${formatPrice(low)} – ${formatPrice(high)}`;
  }

  // ============================================================
  // FUNCTION 1 — GRID
  // Requires the companion Liquid snippet update.
  // ============================================================

  function initGrid() {
    if (window.__twwGridInit) return;
    window.__twwGridInit = true;

    const cachePrefix = CONFIG.CACHE_PREFIX + 'eligible-v2-';

    let running = false;
    let queued = false;
    let timer;

    function resetPrice(card) {
      card.querySelectorAll('.tww-grid-price-ui').forEach(element => {
        element.remove();
      });

      card.querySelectorAll('[data-tww-native-price]').forEach(element => {
        element.hidden = false;
      });
    }

    function renderPrice(
      card,
      originals,
      finals,
      type,
      partial
    ) {
      resetPrice(card);

      if (!originals.length || originals.length !== finals.length) {
        return;
      }

      const nativePrices = card.querySelectorAll(
        '[data-tww-native-price]'
      );

      nativePrices.forEach(nativePrice => {
        const ui = document.createElement('div');

        ui.className =
          `tww-grid-price-ui tww-grid-price-ui--${type}`;

        const original = document.createElement('div');
        original.className = 'tww-grid-original-price';
        original.textContent = formatRange(
          Math.min(...originals),
          Math.max(...originals)
        );

        const final = document.createElement('div');
        final.className = 'tww-grid-discounted-price';
        final.textContent = formatRange(
          Math.min(...finals),
          Math.max(...finals)
        );

        ui.append(original, final);

        if (partial) {
          const note = document.createElement('div');
          note.className = 'tww-grid-selected-options';
          note.textContent =
            card.dataset.selectedOptionsLabel || 'Selected options';

          ui.append(note);
        }

        nativePrice.hidden = true;
        nativePrice.insertAdjacentElement('afterend', ui);
      });
    }

    function recordFor(card) {
      const ids = (card.dataset.variantIds || '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean);

      const rawPrices = (card.dataset.variantPrices || '').split(',');
      const prices = rawPrices.map(value => Number(value) / 100);
      const expected = Number(card.dataset.variantCount);

      if (
        !Number.isInteger(expected) ||
        expected < 1 ||
        expected !== ids.length ||
        prices.length !== ids.length ||
        new Set(ids).size !== ids.length ||
        ids.some(id => !/^\d+$/.test(id)) ||
        rawPrices.some(value => !value.trim()) ||
        prices.some(value => !Number.isFinite(value) || value < 0)
      ) {
        throw new Error('Incomplete or invalid Liquid variant data');
      }

      return {
        card,
        ids,
        prices,
      };
    }

    async function fetchBatch(ids) {
      const cacheKey =
        cachePrefix + [...ids].sort().join(',');

      try {
        const cached = JSON.parse(
          sessionStorage.getItem(cacheKey) || 'null'
        );

        if (
          cached &&
          Date.now() - cached.timestamp < CONFIG.CACHE_TTL_MS &&
          cached.values?.length === ids.length &&
          ids.every(id =>
            cached.values.some(value => value.id === id)
          )
        ) {
          return cached.values;
        }
      } catch (error) {
        console.warn('[Grid] Cache unavailable; checking API.');
      }

      const query = `
        mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            userErrors {
              field
              message
            }
            cart {
              lines(first: 250) {
                edges {
                  node {
                    merchandise {
                      ... on ProductVariant {
                        id
                      }
                    }
                    discountAllocations {
                      discountedAmount {
                        amount
                      }
                      ... on CartAutomaticDiscountAllocation {
                        title
                      }
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                }
              }
            }
          }
        }
      `;

      const abort = new AbortController();

      const timeout = setTimeout(
        () => abort.abort(),
        15000
      );

      let values;

      try {
        const response = await fetch(CONFIG.STOREFRONT_URL, {
          method: 'POST',
          signal: abort.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token':
              CONFIG.STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query,
            variables: {
              input: {
                lines: ids.map(id => ({
                  quantity: 1,
                  merchandiseId:
                    `gid://shopify/ProductVariant/${id}`,
                })),
              },
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.errors?.length) {
          throw new Error(
            data.errors.map(error => error.message).join('; ')
          );
        }

        const payload = data.data?.cartCreate;

        if (payload?.userErrors?.length) {
          throw new Error(
            payload.userErrors
              .map(error => error.message)
              .join('; ')
          );
        }

        const lines = payload?.cart?.lines;

        if (
          !lines ||
          !Array.isArray(lines.edges) ||
          lines.pageInfo?.hasNextPage !== false
        ) {
          throw new Error('Incomplete API cart lines');
        }

        values = lines.edges.map(({ node }) => {
          if (
            !node.merchandise?.id ||
            !Array.isArray(node.discountAllocations)
          ) {
            throw new Error('Invalid API cart line');
          }

          const allocations = node.discountAllocations.filter(
            item => !item.title?.toUpperCase().includes('TRADE')
          );

          if (
            allocations.some(item =>
              !Number.isFinite(
                Number(item.discountedAmount?.amount)
              ) ||
              Number(item.discountedAmount.amount) < 0
            )
          ) {
            throw new Error('Invalid discount allocation');
          }

          return {
            id: node.merchandise.id.split('/').pop(),
            saving: Math.max(
              0,
              ...allocations.map(item =>
                Number(item.discountedAmount.amount)
              )
            ),
          };
        });

        if (
          values.length !== ids.length ||
          new Set(values.map(value => value.id)).size !== ids.length ||
          !ids.every(id =>
            values.some(value => value.id === id)
          )
        ) {
          throw new Error(
            'API did not return every requested variant'
          );
        }
      } finally {
        clearTimeout(timeout);
      }

      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            timestamp: Date.now(),
            values,
          })
        );
      } catch (error) {
        console.warn('[Grid] Could not cache discount results.');
      }

      return values;
    }

    function apply(record, savings) {
      const {
        card,
        ids,
        prices,
      } = record;

      const eligible = ids.map((id, index) => ({
        original: prices[index],
        saving: savings.get(id),
      }));

      if (
        eligible.some(item =>
          !Number.isFinite(item.saving) ||
          item.saving > item.original
        )
      ) {
        throw new Error('Missing or invalid variant saving');
      }

      const sale = eligible.filter(item => item.saving > 0);

      card.querySelectorAll(
        '.product-badge--sale-js, .product-badge--trade-js'
      ).forEach(element => element.remove());

      const liquidSale = card.querySelector(
        '.product-badge--liquid-sale'
      );

      const liquidTrade = card.querySelector(
        '.product-badge--liquid-trade'
      );

      const badgeTarget =
        card.querySelector('figure[data-product-item-image]') ||
        card;

      if (sale.length) {
        liquidTrade?.remove();

        const metafieldPct = Number(card.dataset.salePct);

        const pct = metafieldPct > 0
          ? metafieldPct
          : Math.max(
            ...sale.map(item =>
              item.saving / item.original * 100
            )
          );

        if (!liquidSale) {
          const badge = document.createElement('span');

          badge.className =
            `product-badge ${getBadgeClass(pct)} ` +
            'product-badge--sale-js';

          badge.textContent = getBadgeLabel(pct);
          badgeTarget.appendChild(badge);
        }

        // Both ranges use exactly the same discounted variants.
        renderPrice(
          card,
          sale.map(item => item.original),
          sale.map(item => item.original - item.saving),
          getPriceUIClass(pct),
          sale.length < ids.length
        );
      } else {
        liquidSale?.remove();

        if (isTradeCustomer(card)) {
          if (!liquidTrade) {
            const badge = document.createElement('span');

            badge.className =
              'product-badge product-badge--trade ' +
              'product-badge--trade-js';

            badge.textContent = 'Trade';
            badgeTarget.appendChild(badge);
          }

          renderPrice(
            card,
            prices,
            prices.map(price =>
              price * (1 - CONFIG.TRADE_DISCOUNT_RATE)
            ),
            'trade',
            false
          );
        } else {
          liquidTrade?.remove();
          resetPrice(card);
        }
      }
    }

    async function reconcile() {
      if (running) {
        queued = true;
        return;
      }

      running = true;

      try {
        const cards = Array.from(
          document.querySelectorAll(
            '[data-product-card][data-variant-id]' +
            ':not([data-badge-checked])'
          )
        );

        const records = [];

        cards.forEach(card => {
          card.setAttribute('data-badge-checked', 'pending');

          try {
            if (!card.querySelector('[data-tww-native-price]')) {
              throw new Error(
                'Companion Liquid update missing or ' +
                'price intentionally suppressed'
              );
            }

            records.push(recordFor(card));
          } catch (error) {
            resetPrice(card);
            card.setAttribute('data-badge-checked', 'error');

            console.warn(
              '[Grid] Native pricing retained:',
              error.message,
              card
            );
          }
        });

        const ids = [
          ...new Set(
            records.flatMap(record => record.ids)
          ),
        ];

        const savings = new Map();
        const failed = new Set();

        // Sequential bounded batches avoid unbounded parallel
        // requests and duplicate cart lines.
        for (let index = 0; index < ids.length; index += 100) {
          const batch = ids.slice(index, index + 100);

          try {
            const values = await fetchBatch(batch);

            values.forEach(value => {
              savings.set(value.id, value.saving);
            });
          } catch (error) {
            batch.forEach(id => failed.add(id));

            console.warn(
              '[Grid] Batch failed; native pricing retained:',
              error.message
            );
          }
        }

        records.forEach(record => {
          try {
            if (record.ids.some(id => failed.has(id))) {
              throw new Error('Variant check incomplete');
            }

            apply(record, savings);

            record.card.setAttribute(
              'data-badge-checked',
              'true'
            );
          } catch (error) {
            resetPrice(record.card);

            record.card.setAttribute(
              'data-badge-checked',
              'error'
            );

            console.warn(
              '[Grid] Native pricing retained:',
              error.message,
              record.card
            );
          }
        });
      } finally {
        running = false;

        if (queued) {
          queued = false;
          schedule();
        }
      }
    }

    function schedule() {
      clearTimeout(timer);

      timer = setTimeout(
        reconcile,
        CONFIG.OBSERVER_DEBOUNCE
      );
    }

    const observer = new MutationObserver(() => {
      if (
        document.querySelector(
          '[data-product-card][data-variant-id]' +
          ':not([data-badge-checked])'
        )
      ) {
        schedule();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    [
      'page:load',
      'page:change',
      'turbo:load',
      'turbolinks:load',
      'shopify:section:load',
    ].forEach(event => {
      document.addEventListener(event, schedule);
    });

    schedule();
  }

  // ============================================================
  // FUNCTION 2 — PRODUCT PAGE
  // Unchanged from the replacement supplied earlier.
  // ============================================================

  function initProductPage() {
    if (window.__twwProductInit) return;
    window.__twwProductInit = true;

    const hiddenElements = new Map();

    let timer;
    let retryTimer;
    let readinessAttempts = 0;
    let requestAttempts = 0;
    let activeKey = '';
    let requestSequence = 0;
    let controller = null;
    let result = null;
    let renderedKey = '';

    function restoreUI() {
      document.getElementById('tww-discount-ui')?.remove();

      hiddenElements.forEach((display, element) => {
        element.style.display = display;
        delete element.dataset.twwHidden;
      });

      hiddenElements.clear();
      renderedKey = '';
    }

    function hide(element) {
      if (!element || hiddenElements.has(element)) return;

      hiddenElements.set(element, element.style.display);
      element.style.display = 'none';
      element.dataset.twwHidden = 'true';
    }

    function getState() {
      const selection = document.querySelector(
        CONFIG.VARIANT_SELECTION_SELECTOR
      );

      const select = document.querySelector(
        CONFIG.VARIANT_SELECT_SELECTOR
      );

      const variantId =
        selection?.getAttribute('variant') || select?.value;

      const priceEl = document.querySelector(
        CONFIG.PRICE_SELECTOR
      );

      if (
        !variantId ||
        !/^\d+$/.test(variantId) ||
        !priceEl
      ) {
        return null;
      }

      const compareEl = document.querySelector(
        `${CONFIG.COMPARE_AT_SELECTOR} [data-price-compare]`
      );

      let price = parseFloat(
        compareEl?.textContent.replace(/[^0-9.]/g, '') || '0'
      );

      if (!(price > 0) && select?.selectedOptions?.[0]) {
        const match = select.selectedOptions[0].textContent.match(
          /\$([\d,]+(?:\.\d+)?)/
        );

        if (match) {
          price = parseFloat(match[1].replace(/,/g, ''));
        }
      }

      if (!(price > 0)) {
        price = parseFloat(
          priceEl.textContent.replace(/[^0-9.]/g, '')
        );
      }

      if (!(price > 0) || !Number.isFinite(price)) {
        return null;
      }

      const wrapper = document.querySelector('[data-collections]');
      const trade = isTradeCustomer(wrapper);

      return {
        variantId,
        price,
        priceEl,
        trade,
        key: `${variantId}:${price}:${trade}`,
      };
    }

    function render(state) {
      if (!result || result.key !== state.key) return;

      const existing = document.getElementById('tww-discount-ui');

      if (
        renderedKey === state.key &&
        existing &&
        existing.previousElementSibling === state.priceEl
      ) {
        return;
      }

      restoreUI();

      if (!(result.amount > 0)) return;

      const original = state.price;
      const saving = result.amount;

      if (
        !Number.isFinite(saving) ||
        saving > original
      ) {
        console.warn(
          '[Product] Invalid saving; retaining native price.',
          { variantId: state.variantId }
        );

        return;
      }

      const trade =
        result.title === CONFIG.TRADE_DISCOUNT_TITLE;

      const pct = saving / original * 100;

      const wrapper = document.createElement('div');
      wrapper.id = 'tww-discount-ui';
      wrapper.style.cssText =
        'display:flex; align-items:start; gap:10px;';

      const badge = document.createElement('div');

      badge.className =
        `product-page-badge ${
          trade
            ? 'product-badge--trade'
            : getBadgeClass(pct)
        }`;

      badge.textContent = trade
        ? 'Trade'
        : getBadgeLabel(pct);

      const details = document.createElement('div');
      details.className = 'tww-price-details';

      const row = document.createElement('div');
      row.className = 'tww-price-row';

      const originalEl = document.createElement('span');
      originalEl.className = 'tww-original-price';
      originalEl.textContent = formatPrice(original);

      const finalEl = document.createElement('span');
      finalEl.className = 'tww-final-price';
      finalEl.textContent = formatPrice(original - saving);

      row.append(
        originalEl,
        document.createTextNode(' '),
        finalEl
      );

      const label = document.createElement('div');
      label.className = 'tww-discount-label';
      label.textContent =
        `${result.title} (−${formatPrice(saving)})`;

      details.append(row, label);
      wrapper.append(badge, details);

      const scope = state.priceEl.closest('.product-details');

      hide(state.priceEl);
      hide(scope?.querySelector(CONFIG.COMPARE_AT_SELECTOR));

      scope?.querySelectorAll(
        '.productitem--badge, .product__badge, ' +
        '.product-badge:not(.product-page-badge)'
      ).forEach(hide);

      state.priceEl.insertAdjacentElement('afterend', wrapper);
      renderedKey = state.key;
    }

    async function requestDiscount(state) {
      requestAttempts++;

      const sequence = ++requestSequence;
      const abort = new AbortController();

      controller = abort;

      const timeout = setTimeout(
        () => abort.abort(),
        15000
      );

      const query = `
        mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            userErrors {
              field
              message
            }
            cart {
              lines(first: 1) {
                edges {
                  node {
                    discountAllocations {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                      ... on CartAutomaticDiscountAllocation {
                        title
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(CONFIG.STOREFRONT_URL, {
          method: 'POST',
          signal: abort.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token':
              CONFIG.STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query,
            variables: {
              input: {
                lines: [
                  {
                    quantity: 1,
                    merchandiseId:
                      `gid://shopify/ProductVariant/${state.variantId}`,
                  },
                ],
              },
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.errors?.length) {
          throw new Error(
            data.errors.map(error => error.message).join('; ')
          );
        }

        const payload = data.data?.cartCreate;

        if (payload?.userErrors?.length) {
          throw new Error(
            payload.userErrors
              .map(error => error.message)
              .join('; ')
          );
        }

        const line = payload?.cart?.lines?.edges?.[0]?.node;

        if (
          !line ||
          !Array.isArray(line.discountAllocations)
        ) {
          throw new Error(
            'Missing cart line or discount allocation data'
          );
        }

        const allocations = line.discountAllocations;

        if (
          allocations.some(item =>
            !Number.isFinite(
              Number(item.discountedAmount?.amount)
            )
          )
        ) {
          throw new Error('Invalid discount amount');
        }

        const best = allocations
          .filter(item =>
            !item.title?.toUpperCase().includes('TRADE')
          )
          .reduce((previous, item) => {
            return (
              !previous ||
              Number(item.discountedAmount.amount) >
                Number(previous.discountedAmount.amount)
            )
              ? item
              : previous;
          }, null);

        const amount = best
          ? Number(best.discountedAmount.amount)
          : 0;

        const tradeAmount = state.trade
          ? state.price * CONFIG.TRADE_DISCOUNT_RATE
          : 0;

        const current = getState();

        if (
          sequence !== requestSequence ||
          current?.key !== state.key
        ) {
          return;
        }

        result = {
          key: state.key,
          amount: Math.max(amount, tradeAmount),
          title: tradeAmount > amount
            ? CONFIG.TRADE_DISCOUNT_TITLE
            : (best?.title || 'Discount'),
        };

        requestAttempts = 0;
        render(current);

        console.info(
          '[Product] Discount check complete.',
          {
            variantId: state.variantId,
            amount: result.amount,
          }
        );
      } catch (error) {
        if (sequence !== requestSequence) return;

        console.warn(
          '[Product] Discount check failed; native pricing retained.',
          {
            variantId: state.variantId,
            message: error.message,
          }
        );

        if (requestAttempts < 3) {
          clearTimeout(retryTimer);

          retryTimer = setTimeout(
            schedule,
            requestAttempts * 2000
          );
        }
      } finally {
        clearTimeout(timeout);

        if (sequence === requestSequence) {
          controller = null;

          const current = getState();

          if (current && current.key !== activeKey) {
            schedule();
          }
        }
      }
    }

    function check() {
      const state = getState();

      if (!state) {
        if (activeKey) {
          ++requestSequence;
          controller?.abort();
          controller = null;
          activeKey = '';
          result = null;
          restoreUI();
        }

        if (readinessAttempts++ < 20) {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(schedule, 500);
        }

        return;
      }

      readinessAttempts = 0;

      if (state.key !== activeKey) {
        ++requestSequence;
        controller?.abort();
        controller = null;

        clearTimeout(retryTimer);

        activeKey = state.key;
        result = null;
        requestAttempts = 0;

        restoreUI();
      }

      if (result?.key === state.key) {
        return render(state);
      }

      if (!controller && requestAttempts < 3) {
        requestDiscount(state);
      }
    }

    function schedule() {
      clearTimeout(timer);

      timer = setTimeout(
        check,
        CONFIG.RUN_CHECK_DEBOUNCE
      );
    }

    const observer = new MutationObserver(mutations => {
      const relevant = mutations.some(mutation => {
        const element = mutation.target.nodeType === 1
          ? mutation.target
          : mutation.target.parentElement;

        return !element?.closest('#tww-discount-ui');
      });

      if (relevant) schedule();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['variant'],
    });

    document.addEventListener('change', event => {
      if (
        event.target instanceof Element &&
        event.target.matches(
          '[name="id"], .product-form__input input, select[name="id"]'
        )
      ) {
        schedule();
      }
    });

    [
      'variant:changed',
      'variantChange',
      'page:load',
      'page:change',
      'turbo:load',
      'turbolinks:load',
      'shopify:section:load',
    ].forEach(event => {
      document.addEventListener(event, () => {
        readinessAttempts = 0;
        schedule();
      });
    });

    window.addEventListener('online', () => {
      requestAttempts = 0;
      schedule();
    });

    schedule();
  }

  // ============================================================
  // BOOTSTRAP
  // ============================================================

  function bootstrap() {
    if (
      document.querySelector(
        '[data-product-card][data-variant-id]'
      )
    ) {
      initGrid();
    }

    initProductPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      bootstrap,
      { once: true }
    );
  } else {
    bootstrap();
  }

})();
